/**
 * Builds and positions the floating per-image size/float popover.
 */

import { Roles } from "../../constants/roles";
import { Actions } from "../../constants/actions";
import { translator } from "../../translation/translator";
import { WysiwygForm } from "../wysiwyg-form";

export const IMAGE_SIZE_MIN = 10;
export const IMAGE_SIZE_MAX = 100;
export const IMAGE_SIZE_STEP = 10;
export const IMAGE_SIZE_DEFAULT = 50;

// Legacy preset classes from the initial version of this feature (25/50/75/100%).
// Kept here only so already-saved content still resolves to a sensible slider value.
export const IMAGE_SIZE_CLASS_PREFIX = "ql-img-size-";
const LEGACY_SIZE_VALUES = ["25", "50", "75", "100"];

// "none" removes the float/center class outright rather than applying one of its own,
// so it isn't part of this list — see applyFloat() in the handler.
export const IMAGE_FLOAT_DIRECTIONS = ["left", "right", "center"] as const;
export const IMAGE_FLOAT_CLASS_PREFIX = "ql-img-float-";

const FLOAT_BUTTONS: Array<[string, string, string]> = [
    ["none", "wysiwyg_image_float_none", "No wrap"],
    ["left", "wysiwyg_image_float_left", "Wrap text (left)"],
    ["center", "wysiwyg_image_float_center", "Center"],
    ["right", "wysiwyg_image_float_right", "Wrap text (right)"],
];

export class WysiwygImageToolbarRender {
    public static createPopover(hostEl: HTMLElement): HTMLDivElement {
        if (hostEl instanceof HTMLElement) {
            hostEl.classList.add("wysiwyg-image-toolbar-host");
        }

        const popover = document.createElement("div");
        popover.className = "wysiwyg-image-toolbar";
        popover.dataset.role = Roles.WYSIWYG_IMAGE_TOOLBAR;
        popover.hidden = true;

        popover.appendChild(WysiwygImageToolbarRender.buildSizeGroup());

        const floatGroup = document.createElement("div");
        floatGroup.className = "wysiwyg-image-toolbar__group";
        FLOAT_BUTTONS.forEach(([value, key, fallback]) => {
            floatGroup.appendChild(WysiwygImageToolbarRender.buildButton(Actions.SET_IMAGE_FLOAT, value, key, fallback));
        });
        popover.appendChild(floatGroup);

        hostEl.appendChild(popover);

        return popover;
    }

    public static getSizeSlider(popover: HTMLElement): HTMLInputElement | null {
        return WysiwygForm.getImageToolbarSizeSlider(popover);
    }

    public static setSizeDisplay(popover: HTMLElement, percent: number): void {
        const slider = WysiwygImageToolbarRender.getSizeSlider(popover);
        if (slider) {
            slider.value = String(percent);
        }
        const label = WysiwygForm.getImageToolbarSizeLabel(popover);
        if (label) {
            label.textContent = `${percent}%`;
        }
    }

    /** Reads the image's current width as a percent, for initializing the slider on open. */
    public static getImageSizePercent(image: HTMLImageElement): number {
        const inlineWidth = image.style.width;
        const inlineMatch = /^(\d+(?:\.\d+)?)%$/.exec(inlineWidth || "");
        if (inlineMatch) {
            return Math.min(IMAGE_SIZE_MAX, Math.max(IMAGE_SIZE_MIN, Math.round(Number(inlineMatch[1]))));
        }

        const legacyValue = LEGACY_SIZE_VALUES.find((value) => image.classList.contains(`${IMAGE_SIZE_CLASS_PREFIX}${value}`));
        if (legacyValue) {
            return Number(legacyValue);
        }

        return IMAGE_SIZE_DEFAULT;
    }

    public static show(popover: HTMLElement, hostEl: HTMLElement, image: HTMLImageElement): void {
        popover.hidden = false;
        popover.classList.add("wysiwyg-image-toolbar--open");
        WysiwygImageToolbarRender.setSizeDisplay(popover, WysiwygImageToolbarRender.getImageSizePercent(image));
        WysiwygImageToolbarRender.position(popover, hostEl, image);
    }

    public static hide(popover: HTMLElement): void {
        popover.hidden = true;
        popover.classList.remove("wysiwyg-image-toolbar--open");
    }

    public static isOpen(popover: HTMLElement): boolean {
        return !popover.hidden;
    }

    private static position(popover: HTMLElement, hostEl: HTMLElement, image: HTMLImageElement): void {
        const hostRect = hostEl.getBoundingClientRect();
        const imageRect = image.getBoundingClientRect();
        const top = imageRect.top - hostRect.top + hostEl.scrollTop;
        const left = imageRect.left - hostRect.left + hostEl.scrollLeft;
        popover.style.top = `${Math.max(0, top)}px`;
        popover.style.left = `${Math.max(0, left)}px`;
    }

    private static buildSizeGroup(): HTMLDivElement {
        const sizeGroup = document.createElement("div");
        sizeGroup.className = "wysiwyg-image-toolbar__group wysiwyg-image-toolbar__group--size";

        const sizeLabel = document.createElement("span");
        sizeLabel.className = "wysiwyg-image-toolbar__group-label";
        sizeLabel.textContent = translator?.getTranslation("wysiwyg_image_size_label") || "Image size";

        const slider = document.createElement("input");
        slider.type = "range";
        slider.className = "wysiwyg-image-toolbar__slider";
        slider.min = String(IMAGE_SIZE_MIN);
        slider.max = String(IMAGE_SIZE_MAX);
        slider.step = String(IMAGE_SIZE_STEP);
        slider.value = String(IMAGE_SIZE_DEFAULT);
        slider.dataset.action = Actions.SET_IMAGE_SIZE;

        const valueLabel = document.createElement("span");
        valueLabel.className = "wysiwyg-image-toolbar__size-label";
        valueLabel.textContent = `${IMAGE_SIZE_DEFAULT}%`;

        sizeGroup.appendChild(sizeLabel);
        sizeGroup.appendChild(slider);
        sizeGroup.appendChild(valueLabel);

        return sizeGroup;
    }

    private static buildButton(action: string, value: string, translationKey: string, fallback: string): HTMLButtonElement {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "wysiwyg-image-toolbar__btn";
        button.dataset.action = action;
        button.dataset.value = value;
        button.textContent = translator?.getTranslation(translationKey) || fallback;
        return button;
    }
}
